import { forwardRef, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { StyledErrorText } from './question.styles';
import {
    trackQuestionEdit,
    trackQuestionSaved,
    trackQuestionShown,
    trackQuestionError
} from './question.tracking';
import {
    QuestionOption,
    QuestionProps,
    FocusableElement,
    AnswerData
} from './question.types';
import { SavedQuestion } from './component/SavedQuestion';
import { BasicQuestion } from './component/BasicQuestion';

export const Question = forwardRef<FocusableElement, QuestionProps>(
    (
        {
            label,
            options,
            initialValue,
            name,
            subtitle,
            action: { onSaved, onEdit, onSubmit, loading = false },
            formatFieldAnswer,
            analyticsPage,
            required,
            autoFocus = true,
            showEditButton
        },
        ref
    ) => {
        const { register, setValue, handleSubmit } = useForm<AnswerData>({
            defaultValues: {
                [name]: initialValue?.value
            }
        });
        const [selectedRadio, setSelectedRadio] = useState<
            QuestionOption | undefined
        >(initialValue);
        const [isSaved, setIsSaved] = useState(!!initialValue);
        const [isError, setIsError] = useState(false);
        const editButtonRef = useRef<HTMLButtonElement>(null);
        const radioButtonRef = useRef<HTMLDivElement>(null);

        const onChange = (value: string) => {
            setIsError(false);
            setSelectedRadio(options.find((option) => option.value === value));
            setValue(name, value);
        };

        const handleEdit = () => {
            setIsSaved(false);

            trackQuestionEdit(name, selectedRadio?.value || '', analyticsPage);

            if (typeof onEdit === 'function') {
                onEdit(name);
            }

            autoFocus &&
                setTimeout(() => {
                    radioButtonRef.current?.focus();
                }, 200);
        };

        const submitForm = handleSubmit(async (data: AnswerData) => {
            setIsError(false);

            if (required && !data?.[name]) {
                setIsError(true);
                return;
            }

            try {
                await onSubmit(data, name);

                setIsSaved(true);

                trackQuestionSaved(
                    name,
                    selectedRadio?.value || '',
                    analyticsPage
                );

                if (typeof onSaved === 'function') {
                    onSaved?.({ name, value: selectedRadio?.value || '' });
                }

                autoFocus &&
                    setTimeout(() => {
                        editButtonRef.current?.focus();
                    }, 200);
                return;
            } catch (e) {
                setIsError(true);
                trackQuestionError(
                    name,
                    selectedRadio?.value || '',
                    analyticsPage
                );
                return;
            }
        });

        useEffect(() => {
            trackQuestionShown(name, analyticsPage);
        }, [analyticsPage, name]);

        if (isSaved && selectedRadio?.label) {
            return (
                <SavedQuestion
                    text={formatFieldAnswer(selectedRadio)}
                    questionLabel={label}
                    onEdit={handleEdit}
                    id={`question-${name}-label`}
                    testId={`answer-${name}`}
                    ref={ref || editButtonRef}
                    showEditButton={showEditButton}
                />
            );
        }

        return (
            <form onSubmit={submitForm}>
                <BasicQuestion
                    name={name}
                    subtitle={subtitle}
                    label={label}
                    ref={ref || radioButtonRef}
                    selectedValue={selectedRadio?.value || ''}
                    options={options}
                    onChange={onChange}
                    register={register}
                />
                <Box pt="$M100" maxWidth="161px" fw>
                    <Button
                        category="primary"
                        type="submit"
                        loading={loading}
                        loadingText="Submitting"
                        fw
                    >
                        Next
                    </Button>
                </Box>
                <div aria-live="polite">
                    {isError && (
                        <Box py="$S400">
                            <StyledErrorText data-testid="question-error-text">
                                Something went wrong.
                            </StyledErrorText>
                        </Box>
                    )}
                </div>
            </form>
        );
    }
);
